// WcCodeFinder — the Whale Chat (WC) cheat sheet inside the portal (from "WC cheat sheet" of the project): pick the
// components of the call, get its WC code and description from the same dictionary the engine uses (call_codes.json).
// Pre-filled from the current code; "Use this code" puts the result on the call. 6 Oct 2026.
import React, { useEffect, useState } from 'react';
import WC from './wcCodes.json';

// wording copied exactly from the website cheat sheet (WC cheat sheet.txt, updated 6 Oct 2026)
const Q = [
  { n: 1, key: 'type', label: 'What kind of call is it?', opts: [['T', 'Tonal'], ['P', 'Pulsed']] },
  { n: 2, key: 'f', label: 'Fundamental frequency?', opts: [['F1', 'Fundamental frequency below 600 Hz'], ['F2', 'Fundamental frequency between 600 Hz and 1600 Hz'],
    ['F3', 'Fundamental frequency between 0 to 1000 Hz'], ['F4', 'Fundamental Frequency above 1600 Hz']] },
  { n: 3, key: 'd', label: 'What is the call duration?', opts: [['S', 'Short duration (less than 0.5 sec)'], ['M', 'Medium duration (between 0.5 s to 1.2 sec)'],
    ['L', 'Long duration (more than 1.2 sec)']] },
  { n: 4, key: 'h', label: 'Harmonics?', tonal: true, opts: [['HNC', 'Harmonics Not Clear, Possibly Present'], ['HCCS', 'Harmonics Clear, Closely Spaced'],
    ['HCFS', 'Harmonics Clear, Far Spaced'], ['HNP', 'Harmonics Not Present']] },
  { n: 5, key: 'c', label: 'Call contour?', tonal: true, opts: [['Con', 'Concave'], ['Cav', 'Convex'], ['Sine', 'Sine'], ['Flat', 'Flat'], ['Up', 'Upsweep'], ['Down', 'Downsweep']] },
];
const ATTR = [['C', 'Chaotic'], ['NR', 'Nonlinearities'], ['CB', 'Combinations']];

export function parseCode(code) {
  const p = String(code || '').split('_');
  const s = { type: p[0] === 'P' ? 'P' : 'T', f: p[1] || '', d: p[2] || '', h: '', c: '', attrs: [] };
  const rest = s.type === 'T' ? p.slice(5) : p.slice(3);
  if (s.type === 'T') { s.h = p[3] || ''; s.c = p[4] || ''; }
  s.attrs = rest.filter((x) => ['C', 'NR', 'CB'].includes(x));
  return s;
}
export function buildCode(s) {
  const parts = [s.type, s.f, s.d];
  if (s.type === 'T') parts.push(s.h, s.c, ...['C', 'NR'].filter((a) => s.attrs.includes(a)));
  if (s.attrs.includes('CB')) parts.push('CB');
  return parts.filter(Boolean).join('_');
}
export const describe = (code) => WC[code] || null;

export default function WcCodeFinder({ code, onUse, extra = null }) {
  const [s, setS] = useState(() => parseCode(code));
  useEffect(() => { setS(parseCode(code)); }, [code]);
  const result = buildCode(s);
  const desc = describe(result);
  const set = (k, v) => setS({ ...s, [k]: v });
  const toggle = (a) => setS({ ...s, attrs: s.attrs.includes(a) ? s.attrs.filter((x) => x !== a) : [...s.attrs, a] });
  return (
    <div className="wcf">
      {Q.filter((q) => !q.tonal || s.type === 'T').map((q) => (
        <fieldset key={q.key} className="wcf-q">
          <legend>{q.n}. {q.label}</legend>
          <div className="wcf-opts">
            {q.opts.map(([v, l]) => (
              <label key={v} className={`wcf-opt ${s[q.key] === v ? 'on' : ''}`}>
                <input type="radio" name={`wcf-${q.key}`} value={v} checked={s[q.key] === v} onChange={() => set(q.key, v)} />
                {l}
              </label>))}
          </div>
        </fieldset>))}
      <fieldset className="wcf-q">
        <legend>6. Call attributes? (tick all that apply)</legend>
        <div className="wcf-opts">
          {ATTR.filter(([a]) => s.type === 'T' || a === 'CB').map(([a, l]) => (
            <label key={a} className={`wcf-opt ${s.attrs.includes(a) ? 'on' : ''}`}>
              <input type="checkbox" checked={s.attrs.includes(a)} onChange={() => toggle(a)} />
              {l}
            </label>))}
        </div>
      </fieldset>
      <div className={`wcf-result ${desc ? '' : 'bad'}`} aria-live="polite">
        <div className="hcr-mono wcf-rcode">Call Code: {result}</div>
        <div className="wcf-rdesc">Description: {desc || 'Call code not found in dictionary'}</div>
        <button type="button" className="ct-btn primary" disabled={!desc || result === code} onClick={() => onUse(result)}>
          {result === code ? 'This is the call’s code' : 'Use this code'}</button>
        {extra && desc && extra(result)}
      </div>
    </div>
  );
}
